import { getGatedIdacBackendUrl } from '../../utilities/env';
import Page from '@atlaskit/page';
import React, { lazy, useCallback, useMemo, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCobrandedComponents } from '../../components/whitebox-cobranding';
import { useAppSelector } from '../../stores/redux.store';
import { useCobranding } from '../../utilities/cobranding/cobranding';
import { LoginConsentType } from './types';
import { useNavigateSoftOrHard } from '../../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../../utilities/fetch-api';
import { captureTaggedException } from '../../utilities/analytics/error-reporting';

export type ConsentProps = {
  onAccept?: () => void;
  onCancel?: () => void;
};

const consentComponents: Record<string, React.FunctionComponent<ConsentProps>> = {
  fedramp: lazy(() => import('./ConsentMessages/fedramp')),
  'a53d6b63-3k57-1408-j79d-0b60b08kb685-privacy': lazy(
    () => import('./ConsentMessages/a53d6b63-3k57-1408-j79d-0b60b08kb685-privacy')
  ),
  '2a85b822-1ba4-44ec-968a-c2f95bf4b469-privacy': lazy(
    () => import('./ConsentMessages/2a85b822-1ba4-44ec-968a-c2f95bf4b469-privacy')
  ),
  '6e62b4f0-a2f3-4cb5-b61c-1bd397640f04-privacy': lazy(
    () => import('./ConsentMessages/6e62b4f0-a2f3-4cb5-b61c-1bd397640f04-privacy')
  ),
};

export async function consentLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/login/consent/state', request, {
    capabilities: ['capability-group--login', 'capability-name--consent-screen'],
  });
}

export const LoginConsentNoticePage: React.FC<{ children?: React.ReactNode }> = () => {
  const { cobranding } = useCobranding();
  const csrfToken = useAppSelector(state => state.csrfToken);
  const { background: Background } = getCobrandedComponents(cobranding);
  const { organisationId, consentType } = useAppSelector(state => state.loginConsent);
  const { search } = useLocation();
  const navigate = useNavigateSoftOrHard();

  const handleRedirectToLogin = useCallback(
    (errorKey: string) => {
      const existingQueryParams = new URLSearchParams(search);
      existingQueryParams.set('errorCode', errorKey);
      const queryParamsWithErrorCode = existingQueryParams.toString();
      navigate(`/login?prompt=true&${queryParamsWithErrorCode}`);
    },
    [search, navigate]
  );

  const handleOnAccept = useCallback(async () => {
    const requestPayload: LoginConsentType = {
      consent: 'provided',
    };

    const path = getGatedIdacBackendUrl(`/v2/login/authorize/consent${search}`);
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(requestPayload),
    });
    switch (response.status) {
      case 200:
        const responseBody = await response.json();
        navigate(responseBody.uri);
        break;
      case 400:
        handleRedirectToLogin('consent-validation-error');
        break;
      default:
        handleRedirectToLogin('consent-validation-error');
        break;
    }
  }, [csrfToken, search, handleRedirectToLogin, navigate]);

  const handleOnCancel = useCallback(() => {
    const existingQueryParams = new URLSearchParams(search);
    existingQueryParams.delete('token');
    existingQueryParams.set('prompt', 'true');
    existingQueryParams.set('errorCode', 'generic-consent-error');
    const queryParamsWithErrorCode = existingQueryParams.toString();
    navigate(`/login?${queryParamsWithErrorCode}`);
  }, [search, navigate]);

  const consentId = useMemo(() => {
    if (organisationId) {
      return `${organisationId}-${consentType}`;
    }
    return consentType;
  }, [consentType, organisationId]);

  const ConsentComponent = consentId && consentComponents[consentId];

  useEffect(() => {
    if (!ConsentComponent) {
      captureTaggedException(
        `[ConsentScreen] no component rendered for organisation: ${organisationId}. Redirected to /login instead.`,
        {
          capabilities: ['capability-group--login', 'capability-name--consent-screen'],
        }
      );
      handleRedirectToLogin('consent-loading-error');
    }
  }, [ConsentComponent, handleRedirectToLogin, organisationId]);

  return (
    <Page>
      <Background>
        <Suspense fallback={null}>
          {ConsentComponent ? (
            <ConsentComponent onAccept={handleOnAccept} onCancel={handleOnCancel} />
          ) : null}
        </Suspense>
      </Background>
    </Page>
  );
};

export default LoginConsentNoticePage;
